import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Slate, Editable, withReact } from "slate-react";
import { createEditor, Transforms, Editor } from "slate";
import { withHistory } from "slate-history";
import styled from "styled-components";
import { Tooltip } from "@material-ui/core";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { shortTimecode } from "../TimeCodeConverter";

const initialValue = [
  {
    type: "timedText",
    speaker: "none",
    start: 0,
    end: 0,
    children: [{ text: "Transcription started..." }],
  },
];

const LiveTranscript = ({
  caseId,
  data,
  streamingKey,
  timecodeOffset = 0,
  currentTime = 0,
  showTimecodes = true,
  showSpeakers = true,
  handleWordClick,
}) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const [value, setValue] = useState(initialValue);
  const [interimText, setInterimText] = useState(null);
  const [apiLoaded, setApiLoaded] = useState(false);

  const containerRef = useRef(null);
  const lastWordEndRef = useRef(0);
  const apiLoadedRef = useRef(false);
  const processedDataRef = useRef(null);
  const paragraphCounterRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (!caseId || !editor) return;

    if (processedDataRef.current === data) return;

    Editor.withoutNormalizing(editor, () => {
      editor.children = [];
    });

    paragraphCounterRef.current = 0;

    let lastEnd = 0;
    const nodes = [];

    if (Array.isArray(data) && data.length > 0) {
      for (const item of data) {
        let textValue = "";

        if (Array.isArray(item.text)) {
          textValue = item.text.map((t) => t.text).join(" ");
        } else if (typeof item.text === "string") {
          textValue = item.text;
        } else if (item.text?.text) {
          textValue = item.text.text;
        }

        if (!textValue) return;

        const start = Number(item.start ?? 0);
        const end = Number(item.end ?? start);
        const speaker = item.speaker || "unknown";

        lastEnd = Math.max(lastEnd, end);
        paragraphCounterRef.current += 1;

        nodes.push({
          type: "timedText",
          id: paragraphCounterRef.current,
          speaker,
          start,
          end,
          previousTimings: `${start} ${end}`,
          children: [{ text: textValue.trim() }],
        });
      }

      if (nodes.length > 0) {
        Editor.withoutNormalizing(editor, () => {
          Transforms.insertNodes(editor, nodes, { at: [0] });
        });
      }
    }

    lastWordEndRef.current = lastEnd;
    processedDataRef.current = data;
    apiLoadedRef.current = true;
    setApiLoaded(true);

    requestAnimationFrame(scrollToBottom);
  }, [caseId, data, editor, scrollToBottom]);

  const groupWordsBySpeaker = (words) => {
    const groups = [];
    let currentGroup = null;

    words.forEach((w) => {
      const speaker = w.speaker ?? "Speaker";
      const text = w.punctuated_word ?? w.word;

      if (!currentGroup || currentGroup.speaker !== speaker) {
        currentGroup = {
          speaker,
          start: w.start,
          end: w.end,
          text,
          isFinalCount: 1,
        };
        groups.push(currentGroup);
      } else {
        currentGroup.text += " " + text;
        currentGroup.end = w.end;
      }
    });

    return groups;
  };

  useEffect(() => {
    if (!caseId || !apiLoaded || !streamingKey) return;

    const ws = new WebSocket("wss://live-stream-transcription.justact.co.in");

    ws.onopen = () => {
      console.log("WebSocket connected for live transcription updates");
      ws.send(
        JSON.stringify({
          type: "subscribe",
          streamKey: streamingKey,
        }),
      );
    };

    ws.onmessage = (event) => {
      try {
        /*

        const payload = JSON.parse(event.data);
        const words = payload?.data?.channel?.alternatives?.[0]?.words || [];
        const isFinal = payload?.is_final;

        if (!words.length) return;

        const newWords = words.filter((w) => w.end > lastWordEndRef.current);
        if (!newWords.length) return;

        const text = newWords
          .map((w) => w.punctuated_word ?? w.word)
          .join(" ")
          .trim();

        if (!text) return;

        const speaker = newWords[0].speaker || "Speaker";
        const start = newWords[0].start;
        const end = newWords[newWords.length - 1].end;

        // Show interim (real-time) text
        if (!isFinal) {
          setInterimText({ text, speaker, start, end });
          requestAnimationFrame(scrollToBottom);
          return;
        }

        // Final confirmed text → add to editor
        lastWordEndRef.current = end;

        Editor.withoutNormalizing(editor, () => {
          const lastIndex = editor.children.length - 1;
          const lastNode = editor.children[lastIndex];

          const isSameSpeaker =
            lastNode?.type === "timedText" && lastNode?.speaker === speaker;

          if (isSameSpeaker && lastIndex >= 0) {
            // Append to last node if same speaker
            Transforms.insertText(editor, " " + text, {
              at: Editor.end(editor, [lastIndex]),
            });
            Transforms.setNodes(editor, { end }, { at: [lastIndex] });
          } else {
            // New speaker → new node
            paragraphCounterRef.current += 1;
            Transforms.insertNodes(
              editor,
              {
                type: "timedText",
                id: paragraphCounterRef.current,
                speaker,
                start,
                end,
                previousTimings: `${start} ${end}`,
                children: [{ text }],
              },
              { at: [editor.children.length] }
            );
          }
        });

        */

        const payload = JSON.parse(event.data);

        const totalLatencyMs = payload?.latencyMs ?? null;

        const words = payload?.data?.channel?.alternatives?.[0]?.words || [];
        const isFinal = payload?.is_final;
        // const speechFinal = payload?.data?.speech_final;
        // const utteranceEnd =
        //   payload?.data?.type.toLowerCase() === "utteranceend" || false;

        if (!words.length) return;

        const newWords = words.filter((w) => w.end > lastWordEndRef.current);

        if (!newWords.length) return;

        const speakerGroups = groupWordsBySpeaker(newWords);

        if (!isFinal) {
          const lastGroup = speakerGroups[speakerGroups.length - 1];

          setInterimText({
            text: lastGroup.text,
            speaker: lastGroup.speaker,
            start: lastGroup.start,
            end: lastGroup.end,
            latencyMs: totalLatencyMs,
          });

          requestAnimationFrame(scrollToBottom);
          return;
        }

        lastWordEndRef.current = newWords[newWords.length - 1].end;

        Editor.withoutNormalizing(editor, () => {
          speakerGroups.forEach(
            ({ speaker, text, start, end, isFinalCount }) => {
              const lastIndex = editor.children.length - 1;
              const lastNode =
                lastIndex >= 0 ? editor.children[lastIndex] : null;
              const prevCount = lastNode?.isFinalCount ?? 0;

              const isSameSpeaker =
                !!lastNode &&
                lastNode.type === "timedText" &&
                lastNode.speaker === speaker &&
                !lastNode.speechFinal &&
                !lastNode.utteranceEnd;

              if (isSameSpeaker && lastIndex >= 0 && prevCount < 3) {
                Transforms.insertText(editor, " " + text, {
                  at: Editor.end(editor, [lastIndex]),
                });
                Transforms.setNodes(
                  editor,
                  { end, isFinalCount: prevCount + 1 },
                  { at: [lastIndex] },
                );
              } else {
                paragraphCounterRef.current += 1;
                Transforms.insertNodes(editor, {
                  type: "timedText",
                  id: paragraphCounterRef.current,
                  speaker,
                  speakerName: `Speaker ${Number(speaker) + 1}`,
                  start,
                  end,
                  previousTimings: `${start} ${end}`,
                  isFinalCount: 1,
                  children: [{ text }],
                });
              }
            },
          );
        });

        setValue(editor.children);
        setInterimText(null);
        requestAnimationFrame(scrollToBottom);
      } catch (err) {
        console.error("WebSocket message parsing error:", err);
      }
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed");
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    return () => {
      ws.close();
    };
  }, [caseId, apiLoaded, streamingKey, editor, scrollToBottom, setValue]);

  const getHighlightKey = useCallback(() => {
    if (!currentTime) return "";
    for (const node of editor.children) {
      const s = Number(node.start);
      const e = Number(node.end);
      if (currentTime >= s && currentTime <= e) return node.previousTimings;
    }
    return "";
  }, [currentTime, editor.children]);

  const renderElement = useCallback(
    (props) => {
      const { element } = props;
      if (element.type === "timedText") {
        return (
          <TimedTextElement
            {...props}
            timecodeOffset={timecodeOffset}
            handleWordClick={handleWordClick}
            showSpeakers={showSpeakers}
            showTimecodes={showTimecodes}
          />
        );
      }
      return <p {...props.attributes}>{props.children}</p>;
    },
    [timecodeOffset, handleWordClick, showSpeakers, showTimecodes],
  );

  const renderLeaf = useCallback(({ attributes, children, leaf }) => {
    const parent = children.props?.parent;
    const style = leaf.color
      ? { backgroundColor: leaf.color, borderRadius: "4px" }
      : {};

    if (leaf.suggestionText || leaf.name) {
      return (
        <Tooltip
          title={
            leaf.name
              ? `${leaf.suggestionText || leaf.role} - ${leaf.name}`
              : leaf.suggestionText
          }
          placement="top"
          arrow
        >
          <span
            className="timecode text"
            style={style}
            data-start={parent?.start}
            data-previous-timings={parent?.previousTimings}
            {...attributes}
          >
            {children}
          </span>
        </Tooltip>
      );
    }

    return (
      <span
        className="timecode text"
        style={style}
        data-start={parent?.start}
        data-previous-timings={parent?.previousTimings}
        {...attributes}
      >
        {children}
      </span>
    );
  }, []);

  return (
    <TranscriptWrapper ref={containerRef}>
      <style scoped>
        {`
          .timecode[data-previous-timings*="${getHighlightKey()}"] {
            color: ${COLORS.BTN_GREEN} !important;
            font-weight: 600 !important;
          }
        `}
      </style>

      <Slate editor={editor} value={value}>
        <Editable
          readOnly
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          onMouseDown={(e) => {
            e.preventDefault();
            editor.selection = null;
          }}
        />

        {interimText && (
          <>
            <InterimLine>
              {showSpeakers && (
                <SpeakerInfo>
                  <ProfileImage
                    src={require("../../../assets/images/userPlaceholder.svg")}
                    alt="speaker"
                  />
                  <ValueSpeaker>
                    {`Speaker ${
                      Number(
                        String(interimText?.speaker).match(/\d+/)?.[0] || 0,
                      ) + 1
                    }`}
                  </ValueSpeaker>
                </SpeakerInfo>
              )}
              {showTimecodes && (
                <ValueTimesCode data-start={interimText.start}>
                  {shortTimecode(interimText.start)}
                </ValueTimesCode>
              )}
              <ValueLatency>{interimText?.latencyMs}</ValueLatency>
            </InterimLine>
            <IsFinalText>{interimText.text}</IsFinalText>
          </>
        )}
      </Slate>
    </TranscriptWrapper>
  );
};

const TimedTextElement = ({
  element,
  attributes,
  children,
  timecodeOffset,
  handleWordClick,
  showSpeakers,
  showTimecodes,
}) => {
  const displayTime = (element.start || 0) + timecodeOffset;

  const s = String(element?.speaker || "").trim();
  const m = s.match(/\d+/);
  const speakerLabel = m ? `Speaker ${m[0]}` : s || "Speaker";

  return (
    <>
      <DrawerContainer contentEditable={false} {...attributes}>
        {showSpeakers && (
          <SpeakerInfo>
            <ProfileImage
              src={require("../../../assets/images/userPlaceholder.svg")}
              alt="speaker"
            />
            {showSpeakers && (
              <ValueSpeaker>
                {/* {`Speaker ${
                Number(String(element?.speaker).match(/\d+/)?.[0] || 0) + 1
              }`} */}
                {speakerLabel}
              </ValueSpeaker>
            )}
          </SpeakerInfo>
        )}

        {showTimecodes && (
          <ValueTimesCode
            onClick={() => handleWordClick(displayTime)}
            data-start={element.start}
          >
            {shortTimecode(displayTime)}
          </ValueTimesCode>
        )}
        <ValueIds>{element?.id}</ValueIds>
      </DrawerContainer>

      <CustomTextAreaOtter onDoubleClick={() => handleWordClick(displayTime)}>
        {children}
      </CustomTextAreaOtter>
    </>
  );
};

const TranscriptWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  background: ${COLORS.PRIMARY_WHITE};
  border-radius: 8px;
  padding: 16px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const InterimLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  opacity: 0.6;
  font-style: italic;
`;

const DrawerContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  user-select: none;
`;

const SpeakerInfo = styled.div`
  display: flex;
  align-items: center;
  min-width: 120px;
`;

const ProfileImage = styled.img`
  width: 28px;
  height: 20px;
  border-radius: 50%;
  object-fit: contain;
  margin-right: 8px;
`;

const ValueSpeaker = styled.span`
  font-size: 15px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
`;

const ValueTimesCode = styled.span`
  min-width: 82px;
  font-size: 15px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
    color: ${COLORS.BTN_GREEN};
  }
`;

const CustomTextAreaOtter = styled.div`
  margin-left: 131px;
  margin-right: 25px;
  margin-bottom: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  line-height: 1.6;
  color: ${COLORS.COLOR_DARK};
`;

const IsFinalText = styled.span`
  min-width: 82px;
  margin-left: 131px;
  margin-right: 25px;
  font-size: 15px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  font-weight: 600;
  font-style: italic;
`;

const ValueIds = styled.span`
  min-width: 10px;
  font-size: 15px;
  margin-right: 5px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
    color: ${COLORS.BTN_GREEN};
  }
`;

const ValueLatency = styled.span`
  min-width: 100px;
  font-size: 15px;
  margin-right: 5px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
    color: ${COLORS.BTN_GREEN};
  }
`;

export default LiveTranscript;
